import { Box, Group, Menu, ScrollArea, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { ResourceAvatar, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconChevronDown, IconSearch, IconX } from '@tabler/icons-react';
import { ReactElement, useEffect, useState } from 'react';

interface ProviderMonitorDropdownProps {
  selectedProvider: Practitioner | null;
  onProviderChange: (provider: Practitioner | null) => void;
}

export function ProviderMonitorDropdown({
  selectedProvider,
  onProviderChange,
}: ProviderMonitorDropdownProps): ReactElement {
  const medplum = useMedplum();
  const currentUser = useMedplumProfile() as Practitioner;
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [debouncedProviderSearch] = useDebouncedValue(providerSearchTerm, 300);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);

  // Fetch practitioners based on search term
  useEffect(() => {
    async function fetchPractitioners() {
      try {
        const results = await medplum.searchResources('Practitioner', {
          _count: 50,
          ...(debouncedProviderSearch && { name: debouncedProviderSearch }),
          ...(currentUser && { '_id:not': getReferenceString(currentUser) }),
        });
        setPractitioners(results);
      } catch {
        // Silently handle error - just show empty list
        setPractitioners([]);
      }
    }

    fetchPractitioners();
  }, [medplum, debouncedProviderSearch, currentUser]);

  const handleClearSelection = () => {
    onProviderChange(null);
    setProviderSearchTerm('');
  };

  const handleSelectProvider = (practitioner: Practitioner) => {
    onProviderChange(practitioner);
    setProviderSearchTerm('');
  };

  return (
    <Menu shadow="md" width={300} position="bottom-end">
      <Menu.Target>
        <UnstyledButton
          style={{
            color: '#12B886',
            fontWeight: 500,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {selectedProvider
            ? `${selectedProvider.name?.[0]?.given?.join(' ')} ${selectedProvider.name?.[0]?.family}`
            : 'View provider inbox'}
          <IconChevronDown size={16} />
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Box p="xs">
          <TextInput
            placeholder="Search providers..."
            value={providerSearchTerm}
            onChange={(e) => setProviderSearchTerm(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            size="sm"
          />
        </Box>
        <Menu.Divider />
        <ScrollArea h={300}>
          {selectedProvider && (
            <>
              <Menu.Item onClick={handleClearSelection} leftSection={<IconX size={20} />} c="#dc3545" bg="#f8f9fa">
                Clear selection
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          {practitioners.length === 0 ? (
            <Text size="sm" c="dimmed" p="md" ta="center">
              No providers found
            </Text>
          ) : (
            practitioners.map((practitioner) => (
              <Menu.Item key={practitioner.id} onClick={() => handleSelectProvider(practitioner)}>
                <Group gap="sm">
                  <ResourceAvatar value={practitioner} size="sm" radius="xl" />
                  <Text size="sm">
                    {practitioner.name?.[0]?.given?.join(' ')} {practitioner.name?.[0]?.family}
                  </Text>
                </Group>
              </Menu.Item>
            ))
          )}
        </ScrollArea>
      </Menu.Dropdown>
    </Menu>
  );
}
